import { addDays, todayIso } from './dates';
import { type LessonItem, useLessons } from './schedule';

/** What a subject usually needs besides the textbook and the notebook. */
const SUBJECT_ITEMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/фізична культура/i, '👟 спортивна форма'],
  [/геометрія/i, '📐 лінійка й циркуль'],
  [/алгебра|математика/i, '📏 лінійка'],
  [/мистецтво/i, '🎨 альбом і фарби'],
  [/технології/i, '🧵 фартух'],
  [/інформатика/i, '🎧 навушники'],
  [/географія/i, '🗺 атлас і контурні карти'],
  [/захист україни/i, '🎽 зручний одяг'],
];

export interface BackpackItem {
  label: string;
  subjects: string[];
  /** said by the teacher for this lesson (lessons.bring) */
  fromTeacher: boolean;
}

/** The things for a day's lessons: the teacher's words first, then what the subjects need. */
export function packBackpack(lessons: ReadonlyArray<LessonItem>): BackpackItem[] {
  const items = new Map<string, BackpackItem>();
  const add = (label: string, subject: string, fromTeacher: boolean) => {
    const item = items.get(label) ?? { label, subjects: [], fromTeacher };
    if (!item.subjects.includes(subject)) item.subjects.push(subject);
    items.set(label, item);
  };
  const held = lessons.filter((l) => l.status === 'scheduled');
  for (const l of held) if (l.bring) add(`📌 ${l.bring}`, l.subject, true);
  for (const l of held) {
    const match = SUBJECT_ITEMS.find(([pattern]) => pattern.test(l.subject));
    if (match) add(match[1], l.subject, false);
  }
  return [...items.values()].sort((a, b) => Number(b.fromTeacher) - Number(a.fromTeacher));
}

/** The next school day after today and what to pack for it. */
export function useBackpack(schoolId: string, studentId: string) {
  const from = addDays(todayIso(), 1);
  const lessons = useLessons(schoolId, { kind: 'student', studentId }, from, addDays(from, 7));
  const day = lessons.data?.find((l) => l.status === 'scheduled')?.date;
  const ofDay = (lessons.data ?? []).filter((l) => l.date === day);
  return {
    isPending: lessons.isPending,
    date: day,
    items: packBackpack(ofDay),
    subjects: [...new Set(ofDay.filter((l) => l.status === 'scheduled').map((l) => l.subject))],
    /** how many lessons that day, and the first of them */
    lessons: ofDay.filter((l) => l.status === 'scheduled').length,
    first: ofDay.find((l) => l.status === 'scheduled'),
  };
}
